import React, { useState } from 'react';
import { CellInfo } from 'react-table';
import PageContainer from 'src/components/PageContainer';
import PageContent from 'src/components/PageContent';
import NavBar from '../NavBar';
import Flex from 'src/components/Flex';
import Button from 'src/components/Button';
import Table from 'src/components/Table';
import Card from 'src/components/Card';
import Icon from 'src/components/Icon';
import { ICONS } from 'src/utils/constants';
import DateComponent from 'src/components/DateComponent';
import { usePrompts, AiPrompt } from 'src/queries/useAiPrompts';
import { parseError } from 'src/utils';
import PromptModal from '../../components/EditAiPromptModal';
import styles from './styles.module.scss';

function AiPrompts() {
    const { data: prompts = [], isLoading, error, refetch } = usePrompts();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
    const [selectedPrompt, setSelectedPrompt] = useState<AiPrompt | null>(null);

    const handleCreatePrompt = () => {
        setModalMode('create');
        setSelectedPrompt(null);
        setIsModalOpen(true);
    };

    const handleEditPrompt = (prompt: AiPrompt) => {
        setModalMode('edit');
        setSelectedPrompt(prompt);
        setIsModalOpen(true);
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedPrompt(null);
    };

    const columns = [
        {
            Header: 'NAME',
            accessor: 'promptName',
            minWidth: 200,
        },
        {
            Header: 'DESCRIPTION',
            accessor: 'promptDescription',
            minWidth: 300,
            Cell: (props: CellInfo) => (
                <div className={styles.descriptionCell}>{props.value || 'No description provided'}</div>
            ),
        },
        {
            Header: 'TYPE',
            accessor: 'promptType',
            minWidth: 150,
            Cell: (props: CellInfo) => <span className={styles.typeBadge}>{props.value}</span>,
        },
        {
            Header: 'CREATED',
            accessor: 'createdAt',
            minWidth: 150,
            Cell: (props: CellInfo) => <DateComponent date={props.value} showDateTime={true} />,
        },
        {
            Header: 'UPDATED',
            accessor: 'updatedAt',
            minWidth: 150,
            Cell: (props: CellInfo) => <DateComponent date={props.value} showDateTime={true} />,
        },
        {
            Header: 'ACTIONS',
            accessor: 'promptId',
            minWidth: 150,
            Cell: (props: CellInfo) => {
                const prompt = prompts.find((p: AiPrompt) => p.promptId === props.value);
                return (
                    <Flex direction="row" align="center">
                        <Button
                            type="small"
                            text="Edit"
                            onClick={() => handleEditPrompt(prompt!)}
                            className={styles.actionButton}
                            data-test-id="edit-prompt-button"
                        />
                    </Flex>
                );
            },
        },
    ];

    return (
        <PageContainer>
            <NavBar />
            <PageContent>
                <Flex direction="column" className={styles.container}>
                    <Flex direction="row" align="center" justify="between" className={styles.header}>
                        <Flex direction="column">
                            <h1 className={styles.pageTitle}>System Prompts</h1>
                            <p className={styles.pageSubtitle}>
                                Manage system-wide prompts used across the Graphium platform
                            </p>
                        </Flex>
                        <Button
                            type="primary"
                            text="Create Prompt"
                            leftIcon={<Icon name={ICONS.PlusButton} className={styles.plusIcon} />}
                            onClick={handleCreatePrompt}
                            data-test-id="create-prompt-button"
                        />
                    </Flex>

                    {error && (
                        <Card className={styles.errorCard} data-test-id="error-card">
                            <div className={styles.errorMessage}>
                                <strong>Error loading prompts:</strong> {parseError(error)}
                                <Button
                                    type="small"
                                    text="Retry"
                                    onClick={() => refetch()}
                                    className={styles.retryButton}
                                    data-test-id="retry-button"
                                />
                            </div>
                        </Card>
                    )}

                    <Card className={styles.tableCard}>
                        <Table
                            columns={columns}
                            data={prompts}
                            isLoading={isLoading}
                            noDataText="No prompts found. Create your first prompt to get started."
                            showPagination={true}
                            defaultPageSize={10}
                        />
                    </Card>
                </Flex>
            </PageContent>
            <PromptModal isOpen={isModalOpen} mode={modalMode} prompt={selectedPrompt} onClose={handleModalClose} />
        </PageContainer>
    );
}

export default AiPrompts;
